import * as React from 'react';
import { Loader } from '../loading/Loader';
import LocalDiv from '../localization/LocalDiv';
import { css } from '@emotion/core';
import { TABLE_STYLE } from '../layout/table/style';
import { Link } from 'react-router-dom';
import { COLORS } from '../globalStyle/colors';
import { useWindowSize } from '../core/hooks/useWindowSize';
import ProjectCard from './ProjectCard';
import { Str } from '../localization/translationKeys';
import { getProjectStatus, statusToColor } from './module';
import { makeRoutes } from '../navigation/routes';
import Msg from '../localization/Msg';
import { Project } from './models';
import { useSelector } from 'react-redux';
import { AppState } from '../core/redux/appState';
import { FLEX_COLUMN, FLEX_CENTERED } from '../layout/flexBox';
import { btnStyle } from '../forms/styles/button';


const styles = {
  root:         css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  loader:       css`
    width: 150px;
    height: 150px;
  `,
  error:        css`
    color: ${COLORS.WHITE};
    background-color: ${COLORS.RED};
    padding: 15px 20px;
  `,
  noProjects:   css`
    text-align: center;
  `,
  // The table fills this container, so size this container to size the table
  tableWrapper: css`
  `,
  table:        css`
  `,
  cells:        {
    name:     css`
      min-width: 310px;
    `,
    deadline: css`
      min-width: 130px;
    `,
    status:   css`
      width: 125px;
    `,
    navBtn:   css`
      width: 145px;
      padding: 5px !important;
    `,
  },
  link:         css`
    display: block;
    margin-left: 5px;
    margin-right: 5px;
  `,
  projectCard:  css`
    margin-bottom: 5px;
  `,
};


type Props = {
  loading?: boolean;
  error?: boolean;
  projects: Project[] | undefined;
  className?: string;
  code: string;
};


const renderEdgeCase = (props: Props): React.ReactNode => {
  if (props.loading) {
    return <Loader css={styles.loader} />;
  }

  if (props.error) {
    return <Msg str={Str.LoadFailed} css={styles.error} />;
  }

  if (!props.projects || props.projects.length <= 0) {
    return <Msg str={Str.NoProjects} css={styles.error} />;
  }

  return null;
};


const renderData = (props: Props, isMobile: boolean, participantId: number): React.ReactNode => {
  const projects = props.projects ?? [];

  if (isMobile) {
    return <div css={css`display: flex; flex-direction: column; align-items: stretch; align-self: stretch;`}>
      {
        projects!.map(project => (
          <ProjectCard
            key={project.projectId}
            project={project}
            projectRoute={makeRoutes(participantId, project.displayProjectId).Project}
            css={styles.projectCard}
          />
        ))
      }
      {
        projects.length === 0 && (
          <div css={[FLEX_COLUMN, FLEX_CENTERED]}>
            <Msg str={Str.NoProjects} css={styles.noProjects} />
          </div>
        )
      }
    </div>;
  }

  return (
    <LocalDiv css={styles.tableWrapper}>
      <table css={[styles.table, TABLE_STYLE]}>
        <thead>
          <tr>
            <th css={styles.cells.name}>
              <Msg str={Str.Project} />
            </th>
            <th css={styles.cells.deadline}>
              <Msg str={Str.SurveyDeadline} />
            </th>
            <th css={styles.cells.status}>
              <Msg str={Str.Status} />
            </th>
            <th css={styles.cells.navBtn} />
          </tr>
        </thead>
        <tbody>
          {
            projects.map(
              project => {
                const status    = getProjectStatus(project);
                const statusKey = project.statusMessageKey;
                const color     = statusToColor(status);

                return (
                  <tr key={project.projectId}>
                    <td css={styles.cells.name}>{project.surveyName}</td>
                    <td css={styles.cells.deadline}>
                      {new Date(project.surveyDeadline).toLocaleDateString(props.code)}
                    </td>
                    <td css={styles.cells.status}>
                      <Msg str={statusKey} />
                    </td>
                    <td css={styles.cells.navBtn}>
                      <Link
                        to={makeRoutes(participantId, project.displayProjectId).Project}
                        css={[btnStyle.primary(color), styles.link]}
                      >
                        <Msg str={Str.Select} />
                      </Link>
                    </td>
                  </tr>
                );
              },
            )
          }
          {
            projects.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <Msg str={Str.NoProjects} />
                </td>
              </tr>
            )
          }
        </tbody>
      </table>
    </LocalDiv>
  );
};


/**
 * ProjectTable: a component that renders the given list of projects in a navigable way. Can also render loading/error
 * state.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectTable: React.FC<Props> = props => {
  const { isMobile }  = useWindowSize();
  const edgeCase      = renderEdgeCase(props);
  const participantId = useSelector<AppState, number>(state => state.auth.currentUser?.personId ?? 0);

  return (
    <div css={styles.root} className={props.className}>
      { // Full
        edgeCase ? edgeCase : renderData(props, isMobile, participantId)
      }
    </div>
  );
};

export default ProjectTable;
